import React, { useState, useEffect, useRef } from "react";
import Grid from "@mui/material/Grid";
import { Typography, Button, Box } from "@mui/material";
import {enqueueSnackbar, useSnackbar} from "notistack";
import LoopIcon from '@mui/icons-material/Loop';
import UploadIcon from '@mui/icons-material/Upload';
import CachedIcon from '@mui/icons-material/Cached';

import CustomAutoComplete from "../CommonComponents/CustomAutoComplete";
import OptionButtonGroup from "../CommonComponents/OptionButtonGroup";
import DividerLabel from "../CommonComponents/DividerLabel";

import DailyMotionForm from "./SocialForms/DailyMotionForm";
import FacebookForm from "./SocialForms/FacebookForm";
import InstagramForm from "./SocialForms/InstagramForm";
import YouTubeForm from "./SocialForms/YouTubeForm";
import TikTokForm from "./SocialForms/TikTokForm";

import {
    Option, SocialAccountOption, SocialPostTypeOption, SocialPlatformNames, EnrichedSocialContentPublication,
    SocialContentPublication
} from "../../../types/socialTypes";
import { CollectionClip, LogicClipDescription } from "../../../types/collectionTypes";
import { generateClipLogicDescriptions, publishLogicClipMaterialization } from "@/queries/clipQueries";

import DailyMotionIcon from "./Icons/Dailymotion"
import InstagramIcon from "./Icons/Instagram"
import FacebookIcon from "./Icons/Facebook"
import YoutubeIcon from "./Icons/Youtube"
import TelegramIcon from "./Icons/Telegram";

import { useSocialPublication, initialPlatformState, defaultLanguages, defaultStyles } from "../../../contexts/SocialPublicationContext";
import { AGGREGATION_USAGE_TYPE } from "./SocialPublish";
import {useIntl} from "react-intl";

const socialIconsDict: any = {
    [SocialPlatformNames.facebook]: <FacebookIcon/>,
    [SocialPlatformNames.instagram]: <InstagramIcon/>,
    [SocialPlatformNames.tiktok]: <></>,
    [SocialPlatformNames.youtube]: <YoutubeIcon/>,
    [SocialPlatformNames.dailyMotion]: <DailyMotionIcon/>,
    [SocialPlatformNames.telegram]: <TelegramIcon/>,
};

interface PlatformSectionProps {
    platform: Option;
    availableAccounts: SocialAccountOption[];
    availablePostTypes: SocialPostTypeOption[];
    logicClip: CollectionClip | null;
    usageType: AGGREGATION_USAGE_TYPE;
    onPresetPublish?: (payload: any) => Promise<void> | void;
    onPublicationAdd: (newPost: any) => void;
    aspectRatioLabel: string;
    supportedPostAspectRatio: any;
    draftPublications: Record<string, SocialContentPublication>;
    onLogicClipDescriptionChange: (logicClipDescription: LogicClipDescription[]) => void;
}

export const PlatformPublishSection: React.FC<PlatformSectionProps> = ({
                                                                           platform, availableAccounts, availablePostTypes, logicClip, usageType, onPresetPublish,
                                                                           onPublicationAdd, aspectRatioLabel, supportedPostAspectRatio, draftPublications, onLogicClipDescriptionChange
                                                                       }) => {
    const isPreset = usageType === AGGREGATION_USAGE_TYPE.PRESET;

    const { platformStates, updatePlatformState, updatePlatformFormState } = useSocialPublication();

    const intl = useIntl();

    const platformIdStr = platform.id as string;
    const currentState = platformStates[platformIdStr] || initialPlatformState;
    const { socialAccount, socialPostType, selectedLanguage, selectedStyle, socialFormState } = currentState;

    const [descriptionsLoading, setDescriptionsLoading] = useState(false);
    const [publicationLoad, setPublicationLoad] = useState(false);

    // Riferimento per evitare di salvare draft di stati non ancora pronti
    const formStateRef = useRef(socialFormState);
    useEffect(() => { formStateRef.current = socialFormState; }, [socialFormState]);

    const filteredPostTypes = availablePostTypes.filter(pt => {
        if (!aspectRatioLabel) return true;
        const restrictions = supportedPostAspectRatio[platform.value as string];
        if (restrictions && restrictions[pt.value as string]) {
            return restrictions[pt.value as string].includes(aspectRatioLabel);
        }
        return true;
    });


    const applyDescriptionsToForm = (descriptions: any[]) => {
        const title = descriptions.find((d: any) => d.style_name === "title" && d.language === selectedLanguage.key)?.description || '';
        const desc = descriptions.find((d: any) => d.style_name === selectedStyle.key && d.language === selectedLanguage.key)?.description || '';

        updatePlatformState(platformIdStr, {
            socialFormState: { ...socialFormState, title, description: desc, caption: desc }
        });
    };


    useEffect(() => {
        const initializeAccountForm = () => {
            if (!socialAccount || isPreset) return;

            const existingDraft = draftPublications[socialAccount.id];

            if (existingDraft) {
                const draftInfo = existingDraft.publish_info;


                const draftLang = defaultLanguages.find(l => l.key === (draftInfo as any).content_language);
                const draftStyle = defaultStyles.find(s => s.key === (draftInfo as any).content_style);

                updatePlatformState(platformIdStr, {
                    socialPostType: availablePostTypes.find(pt => pt.id === existingDraft.social_content_id) || socialPostType,
                    selectedLanguage: draftLang || selectedLanguage,
                    selectedStyle: draftStyle || selectedStyle,
                    socialFormState: {
                        ...socialFormState,
                        title: draftInfo.title || '',
                        description: draftInfo.description || '',
                        caption: (draftInfo as any).caption || draftInfo.description || '',
                        private: draftInfo.private || false,
                        is_created_for_kids: draftInfo.is_created_for_kids || false,
                        channel_id: draftInfo.channel_id || ''
                    }
                });
            } else if (logicClip?.generated_descriptions && logicClip.generated_descriptions.length > 0) {
                applyDescriptionsToForm(logicClip.generated_descriptions);
            }
        };

        initializeAccountForm();
    }, [socialAccount?.id, logicClip?.generated_descriptions]);

    const handleRegenerate = async () => {
        if (!logicClip?.id) return;
        try {
            setDescriptionsLoading(true);
            const data = await generateClipLogicDescriptions(logicClip.id, [selectedLanguage.key], ["title", selectedStyle.key]);

            applyDescriptionsToForm(data);

            const title = data.find((d: any) => d.style_name === "title" && d.language === selectedLanguage.key)?.description || '';
            const desc = data.find((d: any) => d.style_name === selectedStyle.key && d.language === selectedLanguage.key)?.description || '';

            const newFormState = { ...socialFormState, title, description: desc, caption: desc };

            await saveDraftInBackground(newFormState, true);

        } catch (e: any) {
            console.error(e)
            const is503 = e?.response?.status === 503 || e?.status === 503;

            if (is503) {
                enqueueSnackbar('LLM service unavailable. Please try again later.', { variant: 'error' });
            } else {
                enqueueSnackbar('Post Generation Error', { variant: 'error' });
            }
        } finally {
            setDescriptionsLoading(false);
        }
    };

    const buildPayload = (isDraft: boolean, overrideFormState?: any, includeMetadata: boolean = false, overrideLangKey?: string, overrideStyleKey?: string) => {
        const pName = platform.value as SocialPlatformNames;
        const formStateToUse = overrideFormState ||  formStateRef.current;

        const langToUse = overrideLangKey || selectedLanguage.key;
        const styleToUse = overrideStyleKey || selectedStyle.key;

        let payload: any = {
            platform_id: platform.id,
            social_platform_id: platform.id,
            social_account_id: socialAccount!.id,
            post_type: socialPostType!.id,
            social_content_id: socialPostType!.id,
        };

        if (!isDraft || includeMetadata) {
            payload.content_language = langToUse;
            payload.content_style = styleToUse;
            payload.language = langToUse;
            payload.style = styleToUse;
        }

        if (pName === SocialPlatformNames.dailyMotion) payload = { ...payload, ...formStateToUse };
        else if (pName === SocialPlatformNames.facebook) payload = { ...payload, title: formStateToUse.title, description: formStateToUse.description };
        else if (pName === SocialPlatformNames.instagram) payload = { ...payload, caption: formStateToUse.caption };
        else if (pName === SocialPlatformNames.youtube) payload = { ...payload, title: formStateToUse.title, description: formStateToUse.description, privacy_status: formStateToUse.private ? 'private' : 'public' };
        else if (pName === SocialPlatformNames.tiktok) payload = { ...payload, title: formStateToUse.title, privacy_level: formStateToUse.privacy_level };

        return payload;
    };

    const saveDraftInBackground = async (overrideFormState?: any, includeMetadata: boolean = false, overrideLangKey?: string, overrideStyleKey?: string) => {
        if (!socialAccount || !socialPostType || !logicClip?.materializations?.[0]?.id || isPreset) return;

        try {
            const payload = buildPayload(true, overrideFormState, includeMetadata, overrideLangKey, overrideStyleKey);
            const draftPost = await publishLogicClipMaterialization(logicClip.materializations[0].id, payload, true);
            onPublicationAdd(draftPost);
        } catch (error) {
            console.error("Failed to save draft automatically", error);
        }
    };

    const handleFieldBlur = (field: string, value: string | boolean) => {
        saveDraftInBackground();
    };

    const handlePublish = async () => {
        if (!socialAccount || !socialPostType) return;
        setPublicationLoad(true);
        try {
            const payload = buildPayload(false, undefined, true);

            if (isPreset && onPresetPublish) {
                console.log("Verità", payload)
                await onPresetPublish(payload);
            } else if (logicClip?.materializations?.[0]?.id) {
                const newPost = await publishLogicClipMaterialization(logicClip.materializations[0].id, payload, false);
                onPublicationAdd(newPost);
                enqueueSnackbar('Publish Request Accepted!');
            }
        } catch (e) { enqueueSnackbar('Publish Request Refused'); }
        finally { setPublicationLoad(false); }
    };

    const spinAnimation = { animation: "spin 2s linear infinite", "@keyframes spin": { "100%": { transform: "rotate(360deg)" } } };

    const displayFormState = socialFormState;

    const handleSettingChange = (type: 'language' | 'style', newLabel: string) => {

        const newLang = type === 'language' ? defaultLanguages.find(l => l.label === newLabel)! : selectedLanguage;
        const newStyle = type === 'style' ? defaultStyles.find(s => s.label === newLabel)! : selectedStyle;

        let newTitle = socialFormState.title;
        let newDesc = socialFormState.description;


        if (logicClip?.generated_descriptions) {
            const foundTitle = logicClip.generated_descriptions.find((d: any) => d.style_name === "title" && d.language === newLang.key)?.description;
            const foundDesc = logicClip.generated_descriptions.find((d: any) => d.style_name === newStyle.key && d.language === newLang.key)?.description;

            // Sostituiamo i valori nel form solo se abbiamo trovato le rispettive descrizioni
            if (foundTitle !== undefined) newTitle = foundTitle;
            if (foundDesc !== undefined) newDesc = foundDesc;
        }

        const newFormState = { ...socialFormState, title: newTitle, description: newDesc, caption: newDesc };


        updatePlatformState(platformIdStr, {
            selectedLanguage: newLang,
            selectedStyle: newStyle,
            socialFormState: newFormState
        });


        saveDraftInBackground(newFormState, true, newLang.key, newStyle.key);
    };

    return (
        <Box sx={{ p: 2, borderColor: 'divider', borderRadius: 1 }}>
            <DividerLabel label={
                <Box display="flex" alignItems="center" gap={1}>
                    {socialIconsDict[platform.value as string]}
                    <Typography variant="h5" fontWeight="bold" sx={{ textTransform: 'capitalize' }}>
                        {platform.value}
                    </Typography>
                </Box>
            } />

            <Grid container spacing={4} sx={{ mt: 3, mb: 2 }}>
                <Grid size={{ xs: 12, md: 6 }}>
                    <CustomAutoComplete
                        label="Account" options={availableAccounts} value={socialAccount}
                        onChange={(_, v) => updatePlatformState(platformIdStr, { socialAccount: v as Option })}
                        labelField="value" valueField="id"
                        multiple={false}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                    <CustomAutoComplete
                        label="Post Type"
                        options={filteredPostTypes.map(option => ({
                            ...option,
                            value: intl.formatMessage({ id: option.value, defaultMessage: option.value })
                        }))}
                        value={socialPostType ? {
                            ...socialPostType,
                            value: intl.formatMessage({ id: socialPostType.value, defaultMessage: socialPostType.value })
                        } : null}
                        onChange={(_, v) => updatePlatformState(platformIdStr, { socialPostType: v as Option })}
                        labelField="value"
                        valueField="id"
                        multiple={false}
                    />
                </Grid>
            </Grid>

            <Grid container spacing={4}>
                <Grid size={{ xs: 12, md: 6 }}>
                    {platform.value === SocialPlatformNames.dailyMotion &&
                        <DailyMotionForm
                            dailyMotionFormState={displayFormState}
                            disableCompilationFields={isPreset}
                            onFieldChange={(f,v) => updatePlatformFormState(platformIdStr, f, v)}
                            onFieldBlur={handleFieldBlur}
                        />}

                    {platform.value === SocialPlatformNames.facebook &&
                        <FacebookForm
                            defaultFormState={displayFormState}
                            disableCompilationFields={isPreset}
                            onFieldChange={(f,v) => updatePlatformFormState(platformIdStr, f, v)}
                            onFieldBlur={handleFieldBlur}
                        />}

                    {platform.value === SocialPlatformNames.instagram &&
                        <InstagramForm
                            defaultFormState={displayFormState}
                            selectedPostType={socialPostType ? socialPostType.id : ''}
                            disableCompilationFields={isPreset}
                            onFieldChange={(f, v) => updatePlatformFormState(platformIdStr, f, v)}
                            onFieldBlur={handleFieldBlur}
                        />}

                    {platform.value === SocialPlatformNames.youtube &&
                        <YouTubeForm
                            youTubeFormState={displayFormState}
                            disableCompilationFields={isPreset}
                            onFieldChange={(f,v) => updatePlatformFormState(platformIdStr, f, v)}
                            onFieldBlur={handleFieldBlur}
                        />
                    }

                    {platform.value === SocialPlatformNames.tiktok &&
                        <TikTokForm
                            tikTokFormState={displayFormState}
                            disableCompilationFields={isPreset}
                            onFieldChange={(f: string, v: string) => updatePlatformFormState(platformIdStr, f, v)}
                            onFieldBlur={handleFieldBlur}
                        />
                    }
                </Grid>

                <Grid size={{ xs: 12, md: 6 }} display="flex" flexDirection="column" justifyContent="space-between">
                    {platform.value !== SocialPlatformNames.telegram && <Box>
                        <Typography sx={{ color: 'text.primary', fontWeight: 'bold', mb: 2 }}>
                            Text Generation Settings
                        </Typography>

                        <Grid container spacing={2}>
                            <Grid size={{ xs: 12 }} display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
                                <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 70 }}>Language:</Typography>
                                <OptionButtonGroup
                                    options={defaultLanguages.map(l => l.label)}
                                    value={selectedLanguage.label}
                                    onChange={(v) => handleSettingChange('language', v)}
                                />
                            </Grid>
                            <Grid size={{ xs: 12 }} display="flex" alignItems="center" gap={2}>
                                <Typography variant="body2" fontWeight="bold" sx={{ minWidth: 70 }}>Style:</Typography>
                                <OptionButtonGroup
                                    options={defaultStyles.map(s => s.label)}
                                    value={selectedStyle.label}
                                    onChange={(v) => handleSettingChange('style', v)}
                                />
                            </Grid>
                        </Grid>
                    </Box>}

                    <Box display="flex" justifyContent="flex-end" gap={2} mt={4}>
                        {platform.value !== SocialPlatformNames.telegram && <Button
                            variant="contained"
                            size="small"
                            onClick={handleRegenerate}
                            disabled={descriptionsLoading || isPreset}
                            startIcon={descriptionsLoading ? <LoopIcon sx={spinAnimation} /> : <CachedIcon />}
                        >
                            Generate
                        </Button>}
                        <Button
                            variant="contained"
                            size="small"
                            onClick={handlePublish}
                            disabled={publicationLoad || !socialAccount || !socialPostType}
                            startIcon={publicationLoad ? <LoopIcon sx={spinAnimation} /> : <UploadIcon />}
                        >
                            {isPreset ? 'Save Preset' : 'Publish'}
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
};